import type { FunctionComponent, PropsWithChildren, ReactElement } from 'react'
import React, { Fragment, Suspense, useEffect, useState } from 'react'

import type { Result } from '@app/packages/Result/Result'
import { RouterOutlet } from '@app/packages/routing/RouterOutlet'
import type { History, RouterInterface, RouterInterfaceAfterRenderCallback, RouterRouteDescriptor } from '@app/packages/routing/types'
import { useStackContext } from '@app/packages/StackContext/StackContext'
import type { LoadableState } from '@app/packages/thunkSelector/thunkSelector'
import { useThunkSelector } from '@app/packages/thunkSelector/useThunkSelector'

import type { Store } from '@app/store/store'

import { AppErrorBoundary } from '@app/components/AppErrorBoundary/AppErrorBoundary'
import { ContextProvider } from '@app/components/ContextProvider/ContextProvider'
import { CookieBannerOverlay } from '@app/components/CookieBanner/CookieBanner'
import MountContainer from '@app/components/Mount/Mount'

export default function ({
  router: Router,
  routesSelector,
  history,
  store,
  afterRender,
}: {
  router: RouterInterface
  routesSelector: (state: unknown) => Result<LoadableState<RouterRouteDescriptor[]>>
  history?: History
  store: Store
  afterRender?: RouterInterfaceAfterRenderCallback
}) {
  return (
    <ContextProvider store={store} suspense={Sup}>
      <AppErrorBoundary history={history}>
        <WithRoutes selector={routesSelector}>
          {(routes, onSuspend) => (
            <Router afterRender={afterRender} onSuspend={onSuspend} routes={routes}>
              <MountContainer>
                <CookieBannerOverlay>
                  <RouterOutlet />
                </CookieBannerOverlay>
              </MountContainer>
            </Router>
          )}
        </WithRoutes>
      </AppErrorBoundary>
    </ContextProvider>
  )
}

const WithRoutes: FunctionComponent<{
  selector: (state: unknown) => Result<LoadableState<RouterRouteDescriptor[]>>
  children: (routes: RouterRouteDescriptor[], onSuspend?: <P>(p: Promise<P>) => Promise<P>) => ReactElement
}> = ({ selector, children }) => {
  const routes = useThunkSelector(selector).suspend()
  const stackContext = useStackContext()

  return children(routes, stackContext.handleAction)
}

const Sup = ({ children }: PropsWithChildren) => {
  const [S, setS] = useState(() => Fragment)

  useEffect(() => {
    setTimeout(() => {
      setS(() => Suspense)
    })
  }, [])

  return <S>{children}</S>
}
